// Confirm + execute Part-use hard delete. Kept out of the detail editor
// so that file stays under the size hygiene cap.
import { useCallback, useState } from "react";
import { deleteManufacturingMaterialUse } from "@/lib/manufacturing/materialUsesClient";
import type { MfgMaterialUse } from "@/lib/manufacturing/types";

type Pending = {
  use: MfgMaterialUse;
  busy: boolean;
  error: string | null;
  blockers: Array<{ code: string; message: string }>;
};

type Deps = {
  onDeleted: (use: MfgMaterialUse) => void;
};

function parseBlockers(body: unknown): Array<{ code: string; message: string }> {
  if (!body || typeof body !== "object" || !("blockers" in body)) return [];
  const raw = (body as { blockers: unknown }).blockers;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (b): b is { code: string; message: string } =>
      !!b &&
      typeof b === "object" &&
      typeof (b as { code: unknown }).code === "string" &&
      typeof (b as { message: unknown }).message === "string",
  );
}

export function useMaterialUseDeleteFlow(deps: Deps) {
  const [pending, setPending] = useState<Pending | null>(null);

  const request = useCallback((use: MfgMaterialUse) => {
    setPending({ use, busy: false, error: null, blockers: [] });
  }, []);

  const cancel = useCallback(() => {
    setPending((prev) => (prev?.busy ? prev : null));
  }, []);

  const confirm = useCallback(async () => {
    const snapshot = pending;
    if (!snapshot) return;
    setPending({ ...snapshot, busy: true, error: null, blockers: [] });
    const res = await deleteManufacturingMaterialUse({ use_id: snapshot.use.id });
    if (!res.ok) {
      setPending({
        ...snapshot,
        busy: false,
        error: res.error,
        blockers: parseBlockers(res.body),
      });
      return;
    }
    deps.onDeleted(snapshot.use);
    setPending(null);
  }, [pending, deps]);

  return { pending, request, confirm, cancel };
}
